import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CheckCircle2, Mail, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const connected = params.get("outlook") === "connected";

  const load = useCallback(() => {
    api.get("/integrations/outlook/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const isConnected = connected || status?.connected;
  const configured = status?.configured;

  const connect = () => { window.location.assign(`${API}/integrations/outlook/connect?next=onboarding`); };
  const finish = () => navigate("/");

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="mb-6"><Logo /></div>

        {/* progress */}
        <div className="flex items-center gap-2 mb-6 text-xs font-medium">
          <span className="inline-flex items-center gap-1 text-[#5B880C]"><CheckCircle2 className="h-4 w-4" />Password set</span>
          <ArrowRight className="h-3 w-3 text-slate-300" />
          <span className={isConnected ? "text-[#5B880C] inline-flex items-center gap-1" : "text-[#0094A8]"}>{isConnected && <CheckCircle2 className="h-4 w-4" />}Connect email</span>
          <ArrowRight className="h-3 w-3 text-slate-300" />
          <span className={isConnected ? "text-[#0094A8]" : "text-slate-400"}>Done</span>
        </div>

        {isConnected ? (
          <div className="text-center py-4" data-testid="onboarding-complete">
            <CheckCircle2 className="h-14 w-14 text-[#9ACC32] mx-auto" />
            <h2 className="font-display text-2xl font-bold mt-3">Setup Complete</h2>
            <p className="text-sm text-slate-500 mt-1">Your CRM account is ready{status?.email ? ` and ${status.email} is connected` : ""}.</p>
            <Button className="mt-6 w-full" onClick={finish} data-testid="onboarding-finish">Go to my dashboard</Button>
          </div>
        ) : (
          <div data-testid="onboarding-connect-step">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6 text-[#0094A8]" />Connect Your Company Email</h2>
            <p className="text-sm text-slate-500 mt-2">Link your Microsoft 365 mailbox so your emails and calendar stay in sync with the CRM. You'll sign in on Microsoft's own secure page — the CRM never sees your Microsoft password.</p>

            {status == null ? (
              <div className="mt-6 flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Checking…</div>
            ) : configured ? (
              <Button className="mt-6 w-full" onClick={connect} data-testid="onboarding-connect-outlook">
                <Mail className="h-4 w-4" />Connect Microsoft Outlook
              </Button>
            ) : (
              <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-[#B45309]" data-testid="onboarding-outlook-unconfigured">
                Microsoft integration isn't configured yet. Your administrator will enable it shortly — you can finish setup and connect your mailbox later from Integrations.
              </div>
            )}
            {params.get("outlook") === "error" && <p className="mt-3 text-xs text-[#C42607]">Connection failed or was cancelled. Please try again.</p>}

            <button onClick={finish} className="mt-5 w-full text-sm text-slate-400 hover:text-slate-600" data-testid="onboarding-skip">
              {configured ? "I'll connect later →" : "Continue to CRM →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
